import { useEffect, useRef, useState } from "react";
import { Chart } from "react-google-charts";
import { ChartSkeleton } from "./LoadingSkeleton";

/**
 * react-google-charts sets the `data` prop synchronously, but the actual
 * GViz visualization draws asynchronously afterward — there's a real gap
 * (commonly 1-3s) between "React has the data" and "something is visibly
 * on screen." Trusting only the parent's fetch-loading flag leaves that
 * gap blank. This wraps every <Chart> usage with its own `ready` state,
 * driven by the chart's own 'ready' event, and keeps the skeleton visible
 * until the chart has actually painted — including on every subsequent
 * re-render when `data` changes (range change, sort, selection, etc.),
 * not just the first mount.
 */
const GoogleChart = ({ chartType, data, options, width = "100%", height }) => {
  const [ready, setReady] = useState(false);
  const prevDataRef = useRef(data);

  useEffect(() => {
    if (prevDataRef.current !== data) {
      setReady(false);
      prevDataRef.current = data;
    }
  }, [data]);

  return (
    <div style={{ position: "relative" }}>
      {!ready && (
        <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
          <ChartSkeleton />
        </div>
      )}
      <div style={{ opacity: ready ? 1 : 0, transition: "opacity 0.15s ease" }}>
        <Chart
          chartType={chartType}
          data={data}
          options={options}
          width={width}
          height={height}
          chartEvents={[{ eventName: "ready", callback: () => setReady(true) }]}
        />
      </div>
    </div>
  );
};

export default GoogleChart;
